<script setup lang="ts">
import { computed, provide, ref, useTemplateRef, watch } from 'vue';
import { useElementSize, useTimeoutFn } from '@vueuse/core';
import { addPaddingOnScroll } from '@Helpers';

const props = withDefaults(
  defineProps<{
    hasDataTable?: boolean;
    isFullPageForm?: boolean;
    unstyledFooter?: boolean;
    /** The header stops before the close button and wraps; the body moves down by the lines it adds. */
    wrapsHeader?: boolean;
  }>(),
  {
    hasDataTable: false,
    isFullPageForm: false,
  }
);

/** Room left between a wrapped header and the body. */
const HEADER_GAP_PX = 8;
const RESIZE_SETTLE_MS = 250;

const contentElement = useTemplateRef<HTMLElement>('contentTag');
const headerElement = useTemplateRef<HTMLElement>('headerTag');
const mainElement = useTemplateRef('mainTag');
const rootElement = useTemplateRef<HTMLElement>('rootTag');

const { height: contentHeight } = useElementSize(contentElement, undefined, { box: 'border-box' });
const { height: headerHeight } = useElementSize(headerElement, undefined, { box: 'border-box' });
const { isScrollable } = addPaddingOnScroll(mainElement);
const { start: startResizeTimer } = useTimeoutFn(onResizeSettled, RESIZE_SETTLE_MS, { immediate: false });

const isResizing = ref(false);
const wasScrollableBeforeResize = ref(false);

const hasFixedHeight = computed(() => props.hasDataTable || props.isFullPageForm);
const hasScrollPadding = computed(() => (isResizing.value ? wasScrollableBeforeResize.value : isScrollable.value));

const bodyHeight = computed(() => {
  const canAnimateHeight = !hasFixedHeight.value && contentHeight.value > 0;

  return canAnimateHeight ? `${contentHeight.value}px` : undefined;
});

/** How far a wrapped header reaches past the top of the body. The header floats, so the body pushes itself down by this much. */
const headerOverflow = computed(() => {
  const canMeasure = props.wrapsHeader && !!headerElement.value && !!rootElement.value;

  if (!canMeasure) return 0;

  const headerBottom = (headerElement.value?.offsetTop ?? 0) + headerHeight.value;

  return Math.max(headerBottom - (rootElement.value?.offsetTop ?? 0) + HEADER_GAP_PX, 0);
});

provide('formHasErrorTarget', false);

watch(contentHeight, onContentHeightChange);

// EVENTS
function onContentHeightChange(newHeight: number, oldHeight: number) {
  const renderedHeight = rootElement.value?.clientHeight ?? 0;
  const isFirstMeasure = !oldHeight;
  const staysCappedByMaxHeight = Math.min(newHeight, oldHeight) > renderedHeight + 1;
  const shouldSkipResize = hasFixedHeight.value || isFirstMeasure || staysCappedByMaxHeight;

  if (shouldSkipResize) return;

  if (!isResizing.value) wasScrollableBeforeResize.value = isScrollable.value;

  isResizing.value = true;
  startResizeTimer();
}

function onResizeSettled() {
  isResizing.value = false;
}
</script>

<template>
  <div
    ref="rootTag"
    :class="[
      'dialog-content flex flex-col overflow-y-auto scroll-smooth transition-[height] duration-200 ease-out motion-reduce:transition-none',
      hasDataTable ? 'h-[620px]' : '',
      isFullPageForm ? 'h-[95vh]' : '',
    ]"
    :style="{ height: bodyHeight }"
  >
    <header
      ref="headerTag"
      :class="['absolute top-[24px] text-lg font-semibold text-gray-600', { 'max-w-[calc(100%-6rem)]': wrapsHeader }]"
    >
      <slot
        v-if="$slots.header"
        name="header"
      />
    </header>

    <main
      ref="mainTag"
      :class="[
        'h-full',
        isResizing ? 'overflow-hidden' : 'overflow-y-auto',
        { 'scrollToTopModal pr-[25px]': hasScrollPadding },
      ]"
    >
      <div
        ref="contentTag"
        :class="['flow-root', { 'h-full': hasFixedHeight }]"
      >
        <div
          v-if="headerOverflow"
          :style="{ height: `${headerOverflow}px` }"
        />

        <slot v-if="$slots.default" />
      </div>
    </main>

    <footer :class="['absolute bottom-6 right-6', unstyledFooter ? 'footer-full' : '']">
      <div :class="['relative flex gap-4 pt-3', unstyledFooter ? '' : 'justify-end']">
        <slot
          v-if="$slots.footer"
          name="footer"
        />
      </div>
    </footer>
  </div>
</template>

<style>
.dialog-content {
  max-height: 68vh;
}

.footer-full {
  width: calc(100% - 3rem);
}

@media (min-height: 800px) {
  .dialog-content {
    max-height: 78vh;
  }
}
</style>
